import React, { useState, useEffect } from 'react';
import { ShoppingCart, Check, Zap, Shield, Star, Loader2, Crosshair, Eye, MessageCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const enCartStorage = {
  getCart: () => {
    const cart = localStorage.getItem('cheat_cart_en');
    return cart ? JSON.parse(cart) : [];
  },
  saveCart: (cart) => {
    localStorage.setItem('cheat_cart_en', JSON.stringify(cart));
  },
  addItem: (item) => {
    const cart = enCartStorage.getCart();
    cart.push(item);
    enCartStorage.saveCart(cart);
  },
};

const ProductsEN = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/products?lang=en`);
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Error loading products', {
        description: 'Please try again later'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product, plan) => {
    try {
      const cartItem = {
        productId: product.id,
        productName: product.name,
        planId: plan.id,
        planType: plan.type,
        price: plan.price,
        originalPrice: plan.original_price || plan.price,
        validity: plan.validity,
        timestamp: Date.now()
      };
      
      enCartStorage.addItem(cartItem);
      
      toast.success('Added to cart!', {
        description: `${product.name} - ${plan.type}`,
        duration: 2000,
      });
      
      setTimeout(() => {
        navigate('/en/cart');
      }, 500);
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Error', {
        description: 'Could not add to cart'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-red-600 animate-spin mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white gaming-title mb-2">LOADING...</h1>
          <p className="text-gray-400">Fetching available products</p>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="min-h-screen bg-black py-16 px-6 relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 gaming-title glow-red">
              OUR PRODUCTS
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Choose the perfect panel to dominate Free Fire
            </p>
          </div>
          <div className="text-center py-20">
            <ShoppingCart className="w-24 h-24 text-red-600/30 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4 gaming-title">COMING SOON</h2>
            <p className="text-gray-400 text-lg">New international products are being added. Stay tuned!</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-16 px-6 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 gaming-title glow-red">
            OUR PRODUCTS
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Choose the perfect panel to dominate Free Fire
          </p>
        </div>

        <div className="space-y-12">
          {products.map((product) => (
            <div
              key={product.id}
              className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden border border-gray-700 hover:border-red-500 transition-all shadow-xl hover:shadow-2xl hover:shadow-red-900/50"
            >
              <div className="grid md:grid-cols-5 gap-0">
                <div className="md:col-span-2 relative bg-gradient-to-br from-red-950/20 to-gray-900 p-6 flex items-center justify-center">
                  {product.popular && (
                    <div className="absolute top-4 left-4 bg-yellow-500 text-black font-bold px-3 py-1.5 rounded-full text-xs z-10">
                      BEST SELLER
                    </div>
                  )}
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-auto rounded-xl shadow-2xl"
                  />
                </div>

                <div className="md:col-span-3 p-8">
                  <h2 className="text-3xl font-bold text-white mb-3">{product.name}</h2>
                  <p className="text-gray-400 mb-6">{product.description}</p>

                  <div className="grid grid-cols-2 gap-2 mb-6">
                    {product.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-300">
                        <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </div>
                    ))}
                  </div>

                  <div className="bg-black/60 rounded-xl p-5 mb-6 border border-red-900/40">
                    <h3 className="text-red-500 font-bold text-sm uppercase tracking-wider mb-3">Panel Highlights</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                        <span className="text-gray-200 text-sm font-medium">Instant delivery</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Crosshair className="w-4 h-4 text-red-400 flex-shrink-0" />
                        <span className="text-gray-200 text-sm font-medium">Legit Aimbot with adjustments</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <span className="text-gray-200 text-sm font-medium">Ultra aggressive Aimbot</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Crosshair className="w-4 h-4 text-orange-400 flex-shrink-0" />
                        <span className="text-gray-200 text-sm font-medium">Freeze Aimbot to kill behind cover</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-blue-400 flex-shrink-0" />
                        <span className="text-gray-200 text-sm font-medium">All possible ESP features</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span className="text-gray-200 text-sm font-medium">Easy install with Discord & WhatsApp support</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {product.plans.map((plan) => {
                      const discount = plan.original_price 
                        ? Math.round(((plan.original_price - plan.price) / plan.original_price) * 100)
                        : 0;
                      
                      return (
                        <div key={plan.id} className="bg-gray-800/50 rounded-xl p-4 border border-gray-700 hover:border-red-500 transition-all">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="text-white font-bold text-lg">{plan.type}</h4>
                                {discount > 0 && (
                                  <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
                                    -{discount}%
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-500 text-sm">{plan.validity}</p>
                            </div>
                            
                            <div className="text-left md:text-right">
                              {plan.original_price && (
                                <p className="text-gray-500 text-sm line-through">$ {plan.original_price.toFixed(2)}</p>
                              )}
                              <p className="text-red-500 font-bold text-2xl">$ {plan.price.toFixed(2)}</p>
                              {discount > 0 && (
                                <p className="text-green-400 text-xs">Save $ {(plan.original_price - plan.price).toFixed(2)}</p>
                              )}
                            </div>
                            
                            <button
                              onClick={() => handleAddToCart(product, plan)}
                              className="w-full md:w-auto bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2"
                            >
                              <ShoppingCart className="w-5 h-5" />
                              <span>Buy</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center bg-gradient-to-r from-red-900/20 via-red-800/20 to-red-900/20 rounded-2xl p-12 border-2 border-red-900/30">
          <h3 className="text-2xl md:text-4xl font-bold text-white mb-4 gaming-title">
            GOT QUESTIONS?
          </h3>
          <p className="text-gray-400 text-lg mb-6">
            Our team is available 24/7 to help you
          </p>
          <Button className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-6 text-lg border-2 border-red-500 shadow-lg">
            CONTACT SUPPORT
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductsEN;
